import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";

import { apiFetch, apiFetchBlob } from "@/lib/api";
import {
  filenameExtension,
  type ControlledDocument,
  type SpreadsheetPreviewResponse,
} from "@/lib/documents";
import { hubPreviewFrameClassName } from "@/lib/hubLayoutClassNames";
import { cn } from "@/lib/utils";

import { StepCadDocumentPreview } from "./StepCadDocumentPreview";
import { isStepCadFilename } from "./stepCadPreview";

type PreviewKind = "pdf" | "html" | "image" | "text" | "spreadsheet" | "step" | "none";

const HTML_EXTENSIONS = new Set(["htm", "html", "xhtml"]);

const TEXT_EXTENSIONS = new Set([
  "asc",
  "txt",
  "csv",
  "md",
  "log",
  "json",
  "xml",
  "css",
  "js",
  "ts",
  "tsx",
  "yaml",
  "yml",
]);

const SPREADSHEET_EXTENSIONS = new Set(["xlsx", "xlsm"]);

const MAX_TEXT_CHARS = 400_000;

function inferPreviewKind(document: ControlledDocument): PreviewKind {
  const mime = (document.content_type ?? "").toLowerCase();
  const extension = (filenameExtension(document.original_filename) ?? "").toLowerCase();
  if (mime.includes("pdf") || extension === "pdf") return "pdf";
  if (mime === "text/html" || mime === "application/xhtml+xml" || HTML_EXTENSIONS.has(extension)) return "html";
  if (mime.startsWith("image/")) return "image";
  if (
    SPREADSHEET_EXTENSIONS.has(extension) ||
    mime.includes("spreadsheetml") ||
    mime.includes("sheet.macroenabled")
  ) {
    return "spreadsheet";
  }
  if (isStepCadFilename(document.original_filename, document.content_type)) return "step";
  if (mime.startsWith("text/") || TEXT_EXTENSIONS.has(extension)) return "text";
  return "none";
}

export function previewQueryKey(documentId: string): readonly unknown[] {
  return ["documents", "preview-blob", documentId];
}

export function spreadsheetPreviewQueryKey(documentId: string, sheetIndex: number): readonly unknown[] {
  return ["documents", "preview-spreadsheet", documentId, sheetIndex];
}

export async function fetchPreviewBlob(
  documentId: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; contentType: string; originalFilename: string | null }> {
  return apiFetchBlob(`/api/documents/${encodeURIComponent(documentId)}/preview`, { signal });
}

export async function fetchPreviewSpreadsheet(
  documentId: string,
  sheetIndex: number,
): Promise<SpreadsheetPreviewResponse> {
  return apiFetch<SpreadsheetPreviewResponse>(
    `/api/documents/${encodeURIComponent(documentId)}/preview-spreadsheet`,
    { query: sheetIndex > 0 ? { sheet: sheetIndex } : undefined },
  );
}

export function DocumentFilePreview({
  document,
  fillAvailableHeight = false,
  headerActions,
}: {
  document: ControlledDocument;
  /** When true, the preview grows to fill the flex parent (used in the document detail modal). */
  fillAvailableHeight?: boolean;
  headerActions?: ReactNode;
}): ReactElement {
  const kind = inferPreviewKind(document);

  const blobQuery = useQuery({
    queryKey: previewQueryKey(document.id),
    queryFn: ({ signal }) => fetchPreviewBlob(document.id, signal),
    enabled: kind === "pdf" || kind === "html" || kind === "image" || kind === "text",
    staleTime: 240_000,
    gcTime: 240_000,
  });

  const objectUrl = useMemo(() => {
    if (!blobQuery.data) return null;
    return URL.createObjectURL(blobQuery.data.blob);
  }, [blobQuery.data]);
  useEffect(() => {
    if (!objectUrl) return undefined;
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const textQuery = useQuery({
    queryKey: ["documents", "preview-text", document.id],
    queryFn: async () => {
      if (!blobQuery.data) throw new Error("Preview data not loaded yet.");
      const text = await blobQuery.data.blob.text();
      const truncated = text.length > MAX_TEXT_CHARS;
      return { text: truncated ? text.slice(0, MAX_TEXT_CHARS) : text, truncated };
    },
    enabled: kind === "text" && !!blobQuery.data,
    staleTime: 240_000,
  });

  const frameClass = hubPreviewFrameClassName(fillAvailableHeight ? "fill" : "inline");

  const frameWrapClass = fillAvailableHeight ? "flex min-h-0 flex-1 flex-col px-2 pb-2 pt-0" : "px-2 pb-2 pt-0";

  if (kind === "step") {
    return (
      <StepCadDocumentPreview
        document={document}
        fillAvailableHeight={fillAvailableHeight}
        headerActions={headerActions}
      />
    );
  }

  if (kind === "none") {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <div
          className={cn(
            "flex flex-col items-center justify-center px-4 py-10",
            fillAvailableHeight ? "min-h-0 flex-1" : "min-h-[240px]",
          )}
        >
          <p className="text-center text-sm text-slate-600">
            No inline preview for this file type. Use Download to open the file locally.
          </p>
        </div>
      </PreviewChrome>
    );
  }

  if (kind === "spreadsheet") {
    return <SpreadsheetDocumentPreview document={document} fillAvailableHeight={fillAvailableHeight} headerActions={headerActions} />;
  }

  if (blobQuery.isPending) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="py-12 text-center text-sm text-slate-500">Loading preview…</p>
      </PreviewChrome>
    );
  }
  if (blobQuery.isError) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="px-3 py-8 text-center text-sm text-red-700">
          {(blobQuery.error as Error).message ?? "Could not load preview."}
        </p>
      </PreviewChrome>
    );
  }

  if (kind === "pdf") {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <div className={frameWrapClass}>
          <iframe src={objectUrl ?? undefined} title={`PDF preview of ${document.original_filename}`} className={frameClass} />
        </div>
      </PreviewChrome>
    );
  }

  if (kind === "html") {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <div className={frameWrapClass}>
          <iframe
            src={objectUrl ?? undefined}
            title={`HTML preview of ${document.original_filename}`}
            className={frameClass}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
          />
        </div>
      </PreviewChrome>
    );
  }

  if (kind === "image") {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <div
          className={cn(
            "flex items-center justify-center overflow-auto px-2 pb-2 pt-0",
            fillAvailableHeight && "min-h-0 flex-1 flex-col",
            !fillAvailableHeight && frameClass,
          )}
        >
          <img
            src={objectUrl ?? undefined}
            alt=""
            className={
              fillAvailableHeight ? "max-h-full max-w-full flex-1 object-contain" : "max-h-[min(88vh,960px)] max-w-full object-contain"
            }
          />
        </div>
      </PreviewChrome>
    );
  }

  if (textQuery.isPending) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="py-12 text-center text-sm text-slate-500">Decoding text…</p>
      </PreviewChrome>
    );
  }
  if (textQuery.isError) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="px-3 py-8 text-center text-sm text-red-700">
          {(textQuery.error as Error).message ?? "Could not load preview."}
        </p>
      </PreviewChrome>
    );
  }

  const payload = textQuery.data;
  return (
    <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
      <div className={cn("overflow-auto px-2 pb-2 pt-0", fillAvailableHeight ? "min-h-0 flex-1 flex-col" : frameClass)}>
        <pre className="whitespace-pre-wrap break-words px-2 pb-1 pt-2 font-mono text-xs leading-relaxed text-slate-800">
          {payload.text}
        </pre>
        {payload.truncated ? (
          <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            Showing the first {MAX_TEXT_CHARS.toLocaleString()} characters only.
          </p>
        ) : null}
      </div>
    </PreviewChrome>
  );
}

function SpreadsheetDocumentPreview({
  document,
  fillAvailableHeight = false,
  headerActions,
}: {
  document: ControlledDocument;
  fillAvailableHeight?: boolean;
  headerActions?: ReactNode;
}): ReactElement {
  const [sheetIndex, setSheetIndex] = useState(0);

  useEffect(() => {
    setSheetIndex(0);
  }, [document.id]);

  const spreadsheetQuery = useQuery({
    queryKey: spreadsheetPreviewQueryKey(document.id, sheetIndex),
    queryFn: () => fetchPreviewSpreadsheet(document.id, sheetIndex),
    staleTime: 240_000,
    gcTime: 240_000,
  });

  const frameClass = fillAvailableHeight
    ? "min-h-0 flex-1 overflow-auto rounded-md border border-slate-200 bg-white"
    : cn(hubPreviewFrameClassName("inline"), "overflow-auto");

  if (spreadsheetQuery.isPending) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="py-12 text-center text-sm text-slate-500">Loading workbook…</p>
      </PreviewChrome>
    );
  }
  if (spreadsheetQuery.isError) {
    return (
      <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
        <p className="px-3 py-8 text-center text-sm text-red-700">
          {(spreadsheetQuery.error as Error).message ?? "Could not load workbook preview."}
        </p>
      </PreviewChrome>
    );
  }

  const preview = spreadsheetQuery.data;
  const showSheetPicker = preview.sheets.length > 1;

  return (
    <PreviewChrome fillAvailableHeight={fillAvailableHeight} title="Preview" headerActions={headerActions}>
      <div className={cn("flex flex-col gap-2 px-2 pb-2 pt-0", fillAvailableHeight && "min-h-0 flex-1")}>
        {showSheetPicker ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {preview.sheets.map((sheet) => {
              const active = sheet.index === sheetIndex;
              return (
                <button
                  key={sheet.index}
                  type="button"
                  onClick={() => setSheetIndex(sheet.index)}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-slate-800 bg-slate-800 text-white"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                  )}
                >
                  {sheet.name}
                </button>
              );
            })}
          </div>
        ) : null}
        <div className={cn(frameClass, fillAvailableHeight && "flex min-h-0 flex-1 flex-col")}>
          <table className="min-w-full border-collapse text-left text-xs text-slate-800">
            <thead className="sticky top-0 z-10 bg-slate-100 shadow-[0_1px_0_0_rgb(226_232_240)]">
              <tr>
                {preview.sheet.columns.map((column) => (
                  <th
                    key={column.key}
                    className="whitespace-nowrap border-b border-slate-200 px-2 py-1.5 font-semibold text-slate-700"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.sheet.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={Math.max(preview.sheet.columns.length, 1)}
                    className="px-2 py-8 text-center text-sm text-slate-500"
                  >
                    No rows to show on this sheet.
                  </td>
                </tr>
              ) : (
                preview.sheet.rows.map((row, rowIndex) => (
                  <tr key={`${preview.sheet.index}-${rowIndex}`} className="odd:bg-white even:bg-slate-50/70">
                    {row.map((value, columnIndex) => (
                      <td
                        key={`${preview.sheet.columns[columnIndex]?.key ?? columnIndex}-${rowIndex}`}
                        className="whitespace-pre-wrap border-b border-slate-100 px-2 py-1 align-top"
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {preview.truncated_rows || preview.truncated_columns ? (
          <p className="text-xs text-slate-500">
            {preview.truncated_rows ? "Showing the first 500 rows only. " : ""}
            {preview.truncated_columns ? "Some columns were omitted from the preview. " : ""}
            Download the file for the full workbook.
          </p>
        ) : null}
      </div>
    </PreviewChrome>
  );
}

function PreviewChrome(props: {
  title: string;
  children: ReactNode;
  fillAvailableHeight?: boolean;
  headerActions?: ReactNode;
}): ReactElement {
  return (
    <div
      className={cn(
        "rounded-lg border border-slate-200 bg-slate-50",
        props.fillAvailableHeight && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{props.title}</h3>
        {props.headerActions ? <div className="flex shrink-0 items-center gap-1">{props.headerActions}</div> : null}
      </div>
      <div className={cn(props.fillAvailableHeight ? "flex min-h-0 flex-1 flex-col overflow-hidden" : undefined)}>
        {props.children}
      </div>
    </div>
  );
}
